import {useCallback, useEffect, useState} from 'react';
import {User} from 'firebase/auth';
import {isAdmin, logout, onAuthChange, signInWithEmail, signInWithGoogle,} from '../services/authService';

interface AuthState {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAdmin: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        try {
          const adminStatus = await isAdmin(user);
          setState({
            user,
            isAdmin: adminStatus,
            loading: false,
            error: adminStatus ? null : 'Access denied. You are not an admin.',
          });
        } catch (error) {
          setState({
            user,
            isAdmin: false,
            loading: false,
            error: 'Failed to verify admin status.',
          });
        }
      } else {
        setState({
          user: null,
          isAdmin: false,
          loading: false,
          error: null,
        });
      }
    });

    return unsubscribe;
  }, []);

  const login = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await signInWithGoogle();
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to sign in with Google.',
      }));
    }
  }, []);

    const loginWithEmail = useCallback(async (email: string, password: string) => {
        setState((prev) => ({...prev, loading: true, error: null}));
        try {
            await signInWithEmail(email, password);
        } catch (error: unknown) {
            const errorCode = (error as { code?: string })?.code;
            let errorMessage = 'Failed to sign in.';
            if (errorCode === 'auth/invalid-credential' || errorCode === 'auth/wrong-password') {
                errorMessage = 'Invalid email or password.';
            } else if (errorCode === 'auth/user-not-found') {
                errorMessage = 'No account found with this email.';
            } else if (errorCode === 'auth/too-many-requests') {
                errorMessage = 'Too many failed attempts. Please try again later.';
            }
            setState((prev) => ({
                ...prev,
                loading: false,
                error: errorMessage,
            }));
        }
    }, []);

  const signOut = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to sign out.',
      }));
    }
  }, []);

  return {
    ...state,
    login,
      loginWithEmail,
    signOut,
  };
}
